// QuickBuy — what a list's Buy carries: the amount, and the trading preset the
// buy goes out on. Discover has one in its toolbar, for the table. Trenches has
// one in every column's head (operator 2026-10-11: "every column needs its own
// Quick buy, not the shared one on top" — this takes back 2026-09-26, one Quick
// buy for the whole board): the compact one, named by its column.
//
// Soft: a recessed track — the bolt, the amount, the GRAM gem, a divider, then
// P1–P3. The amount takes any length; the field starts at its rest width and
// grows with what is typed. Empty means no amount: a list buy without one is
// refused with the amount toast, never sent with a default.
//
// P1–P3 are the trade form's three presets (lib/tradePanel: their slippage),
// not amounts (operator 2026-10-11: "P1 P2 P3 is not an amount preset but a
// trading preset, the amount is not tied to it, and a preset is always chosen,
// P1 by default" — they set the amount to 0.5 / 1 / 2 before). The chosen one
// travels with the Buy to the pair page, where the buy goes out on it. It is on
// one more button too, which steps to the next: a narrow head shows that one
// in place of the three (the stylesheet decides), so the preset a buy goes out
// on is never out of sight.
//
// The three are the one SAVED set (lib/tradePresetStore): a description says the
// slippage of the preset as it is saved now, the figure the buy will carry.

import { LightningIcon } from '@phosphor-icons/react'
import { useId } from 'react'
import { sellPercent } from '../../lib/positionTradeDraft'
import { type TradePreset, slippageForSide } from '../../lib/tradePanel'
import { useTradePresets } from '../../lib/tradePresetStore'
import soft from '../../styles/soft.module.css'
import styles from './QuickBuy.module.css'
import { figs } from './fig'
import { GramMark } from './icons/GramMark'

// what a preset is, for its description: the slippage a buy goes out on (the saved
// one, held to the preset's cap as the trade form holds it)
const presetDescription = (
  presets: readonly TradePreset[],
  index: number,
  side: 'buy' | 'sell'
) => {
  const preset = presets[index]
  const slippage = preset ? ` — ${side} slippage ${slippageForSide(preset, side) / 100}%` : ''
  return `Preset ${index + 1}${slippage}`
}

export function QuickBuy({
  amount,
  onAmountChange,
  preset,
  onPresetChange,
  size,
  of,
  placeholder,
  className,
  side = 'buy',
}: {
  amount: string
  onAmountChange: (amount: string) => void
  /** The trading preset a Buy goes out on: the index of one of the trade
   *  form's presets, 0 for the first. One is always chosen. */
  preset: number
  onPresetChange: (preset: number) => void
  /** 'head': the compact one of a column's head. */
  size?: 'head'
  /** The list it is for, when a screen has more than one: it is in the names. */
  of?: string
  /** What an empty amount says where the word "Amount" has no room (the
   *  Search modal's top row: its field is five figures wide). A head says
   *  "0.0" by itself. */
  placeholder?: string
  className?: string
  side?: 'buy' | 'sell'
}) {
  const head = size === 'head'
  const action = side === 'sell' ? 'sell' : 'buy'
  const unit = side === 'sell' ? '%' : 'GRAM'
  const descriptionId = useId()
  const presets = useTradePresets()
  const count = presets.length
  // a preset the form does not have is the first one
  const chosen = Number.isInteger(preset) && preset >= 0 && preset < count ? preset : 0
  const next = (chosen + 1) % count
  const presetsName = of ? `Trading preset, ${of}` : 'Trading preset'
  return (
    <fieldset
      className={`${soft.track} ${styles.quick}${className ? ` ${className}` : ''}`}
      data-size={size}
      aria-label={of ? `Quick ${action}, ${of}` : `Quick ${action}`}
    >
      <label className={styles.amount}>
        {side === 'sell' && (
          <span id={`${descriptionId}-amount`} hidden>
            Sell 0.01–100% of the primary wallet position; up to 2 decimal places
          </span>
        )}
        <LightningIcon size={head ? 14 : 16} weight="fill" className={styles.bolt} />
        {/* 1ch = one Nunito digit (its figures are all one width) */}
        {/* the amount is a field: its figures centre on the bolt and the gem in
            the field face (soft.figField), as no text-box can cut a field */}
        <input
          className={soft.figField}
          value={amount}
          // A decimal comma is a decimal point: dropped, "0,5" became "05" and
          // every Buy carried 5 (review, 2026-10-12).
          onChange={(e) =>
            onAmountChange(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, ''))
          }
          inputMode="decimal"
          // a head has no room for the word
          placeholder={placeholder ?? (head ? '0.0' : 'Amount')}
          aria-label={
            side === 'sell'
              ? of
                ? `Quick sell percentage, ${of}`
                : 'Quick sell percentage'
              : of
                ? `Quick buy amount, ${of} (${unit})`
                : `Quick buy amount (${unit})`
          }
          aria-describedby={side === 'sell' ? `${descriptionId}-amount` : undefined}
          aria-invalid={
            (side === 'sell' && amount !== '' && sellPercent(amount) === null) || undefined
          }
          style={{ width: `calc(${amount.length}ch + 2px)` }}
        />
        {side === 'sell' ? <span aria-hidden="true">%</span> : <GramMark size={head ? 13 : 14} />}
      </label>
      {/* the three presets are a group of their own: a narrow head folds it */}
      {/* biome-ignore lint/a11y/useSemanticElements: a fieldset inside the track's fieldset would bring a second frame and legend */}
      <span className={styles.presets} role="group" aria-label={presetsName}>
        {presets.map((_, i) => (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: the presets are a fixed list told apart by their place
            key={i}
            type="button"
            className={soft.seg}
            aria-pressed={chosen === i}
            onClick={() => onPresetChange(i)}
            aria-describedby={`${descriptionId}-preset-${i}`}
          >
            {figs(`P${i + 1}`)}
            <span id={`${descriptionId}-preset-${i}`} hidden>
              {presetDescription(presets, i, side)}
            </span>
          </button>
        ))}
      </span>
      {/* …to this one button: the chosen preset, a press steps to the next */}
      <button
        type="button"
        className={`${soft.seg} ${styles.presetOne}`}
        aria-pressed="true"
        aria-label={`Trading preset P${chosen + 1}, switch to P${next + 1}`}
        aria-describedby={`${descriptionId}-preset-${chosen}`}
        onClick={() => onPresetChange(next)}
      >
        {figs(`P${chosen + 1}`)}
      </button>
    </fieldset>
  )
}
